import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Download, XCircle, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [report, setReport] = useState(null);
  const [polls, setPolls] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState('error');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setReport({ title: res.data.report_title, id: res.data.report_id });
      if (res.data.payment_status === 'paid') {
        setState('paid');
        return;
      }
      if (['expired', 'failed'].includes(res.data.payment_status)) {
        setState('error');
        return;
      }
      setPolls((p) => p + 1);
    } catch (err) {
      setState('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'checking') return;
    if (polls >= MAX_POLLS) {
      setState('pending');
      return;
    }
    const t = setTimeout(poll, polls === 0 ? 300 : 2000);
    return () => clearTimeout(t);
  }, [poll, polls, state]);

  const downloadUrl = `${API}/reports/download/${sessionId}`;

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[#f6f9fc] px-5 py-20">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-10 text-center">
        {state === 'checking' && (
          <>
            <Loader2 size={54} className="text-[#1ca5dc] animate-spin mx-auto mb-6" />
            <h1 className="font-heading font-extrabold text-2xl text-[#132b4d] mb-2">Confirming your payment…</h1>
            <p className="text-gray-600">This only takes a moment.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 size={56} className="text-[#1ca5dc] mx-auto mb-6" />
            <h1 className="font-heading font-extrabold text-2xl text-[#132b4d] mb-2">Thank you — payment received</h1>
            <p className="text-gray-600 mb-7">
              {report?.title ? `Your copy of “${report.title}” is ready.` : 'Your report is ready to download.'}
            </p>
            <a href={downloadUrl} className="inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors">
              <Download size={18} /> Download your report
            </a>
            <div className="mt-6">
              <Link to="/reports" className="text-sm text-gray-500 hover:text-[#1ca5dc] inline-flex items-center gap-1">
                <ArrowLeft size={15} /> Back to reports
              </Link>
            </div>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 size={54} className="text-[#1ca5dc] mx-auto mb-6" />
            <h1 className="font-heading font-extrabold text-2xl text-[#132b4d] mb-2">Payment is processing</h1>
            <p className="text-gray-600 mb-7">This can take a little longer. Refresh this page shortly to get your download.</p>
            <button onClick={() => { setPolls(0); setState('checking'); }} className="inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors">
              Check again
            </button>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle size={56} className="text-red-500 mx-auto mb-6" />
            <h1 className="font-heading font-extrabold text-2xl text-[#132b4d] mb-2">We couldn’t confirm this payment</h1>
            <p className="text-gray-600 mb-7">If you were charged, please contact us and we’ll sort it out right away.</p>
            <Link to="/reports" className="inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors">
              <ArrowLeft size={18} /> Back to reports
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
